<template>
  <div class="shop-accounts-list">
    <h1 class="page-title">店务账号管理</h1>
    
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>店务账号列表</h2>
          <el-button type="primary" @click="showAddAccountDialog">
            <el-icon><Plus /></el-icon>
            添加店务账号
          </el-button>
        </div>
      </template>
      
      <div class="card-filter">
        <el-form :inline="true" :model="searchParams" label-position="left">
          <el-form-item label="手机号搜索">
            <el-input
              v-model="searchParams.keyword"
              placeholder="请输入手机号"
              clearable
              @keyup.enter="fetchShopAccounts"
            >
              <template #append>
                <el-button @click="fetchShopAccounts">
                  <el-icon><Search /></el-icon>
                </el-button>
              </template>
            </el-input>
          </el-form-item>
          
          <el-form-item>
            <el-button type="default" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
      </div>
      
      <div class="card-table">
        <el-table
          v-loading="loading"
          :data="shopAccounts"
          border
          style="width: 100%"
          stripe
          size="medium"
        >
          <el-table-column prop="member_type" label="会员类型"  align="center">
            <template #default="scope">
              <el-tag :type="getMemberTypeTag(scope.row.member_type)" size="small">
                {{ scope.row.member_type_name || '未知等级' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="name" label="姓名"  align="center"></el-table-column>
          <el-table-column prop="status" label="状态"  align="center">
            <template #default="scope">
              <el-switch
                v-model="scope.row.status"
                :active-value="'active'"
                :inactive-value="'inactive'"
                @change="(value) => handleStatusChange(scope.row, value)"
              ></el-switch>
            </template>
          </el-table-column>
          <el-table-column prop="phone" label="手机号" width="160" align="center"></el-table-column>
          <el-table-column label="操作" align="center">
            <template #default="scope">
              <el-button type="danger" size="small" @click="handleDeleteAccount(scope.row)">
                <el-icon><Delete /></el-icon>
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      
      <div class="card-footer">
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 添加店务账号弹窗 -->
    <el-dialog
      v-model="addAccountDialogVisible"
      title="添加店务账号"
      width="700px"
      center
    >
      <div class="add-account-dialog">
        <el-form :inline="true" :model="addAccountSearchParams" label-position="left" class="mb-4">
          <el-form-item label="会员手机号">
            <el-input
              v-model="addAccountSearchParams.phone"
              placeholder="请输入会员手机号"
              clearable
              @keyup.enter="fetchAvailableMembers"
            >
              <template #append>
                <el-button @click="fetchAvailableMembers">
                  <el-icon><Search /></el-icon>
                </el-button>
              </template>
            </el-input>
          </el-form-item>
        </el-form>

        <el-table
          v-loading="memberLoading"
          :data="availableMembers"
          border
          style="width: 100%"
          @row-click="handleMemberSelect"
          row-key="id"
          ref="memberTableRef"
          stripe
          size="medium"
        >
          <el-table-column type="selection" width="55" />
          <el-table-column prop="name" label="姓名" width="100" align="center" />
          <el-table-column prop="phone" label="手机号" width="160" align="center" />
          <el-table-column prop="member_type_name" label="会员类型" width="140" align="center" />
        </el-table>

        <div class="dialog-footer mt-4">
          <el-pagination
            :current-page="memberCurrentPage"
            :page-size="memberPageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="memberTotal"
            @size-change="handleMemberSizeChange"
            @current-change="handleMemberCurrentChange"
          />
        </div>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="addAccountDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmAddAccount">确定</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Search, Plus, Delete } from '@element-plus/icons-vue';
import service from '../../api/axios';

// 加载状态
const loading = ref(true);

// 店务账号列表数据
const shopAccounts = ref([]);

// 搜索参数
const searchParams = ref({
  keyword: ''
});

// 分页数据
const currentPage = ref(1);
const pageSize = ref(10);
const total = ref(0);

// 添加店务账号弹窗相关
const addAccountDialogVisible = ref(false);
const addAccountSearchParams = ref({
  phone: ''
});
const availableMembers = ref([]);
const memberLoading = ref(false);
const memberCurrentPage = ref(1);
const memberPageSize = ref(10);
const memberTotal = ref(0);
const memberTableRef = ref(null);

// 获取会员类型标签样式
const getMemberTypeTag = (type) => {
  const tagMap = {
    1: '',
    2: 'primary',
    3: 'success',
    4: 'warning',
    5: 'danger'
  };
  return tagMap[type] || '';
};

// 获取店务账号列表
const fetchShopAccounts = async () => {
  loading.value = true;
  try {
    const response = await service({
      url: '/manage/shop-accounts/list',
      method: 'get',
      params: {
        page: currentPage.value,
        pageSize: pageSize.value,
        keyword: searchParams.value.keyword
      }
    });
    
    // 确保shopAccounts始终是数组
    shopAccounts.value = Array.isArray(response.data) ? response.data : [];
    total.value = response.total || 0;
  } catch (error) {
    ElMessage.error('获取店务账号列表失败：' + (error.message || '未知错误'));
    console.error('获取店务账号列表失败:', error);
    // 出错时确保shopAccounts是数组
    shopAccounts.value = [];
    total.value = 0;
  } finally {
    loading.value = false;
  }
};

// 重置搜索条件
const resetSearch = () => {
  searchParams.value = {
    keyword: ''
  };
  currentPage.value = 1;
  fetchShopAccounts();
};

// 分页大小变化
const handleSizeChange = (size) => {
  pageSize.value = size;
  fetchShopAccounts();
};

// 当前页变化
const handleCurrentChange = (page) => {
  currentPage.value = page;
  fetchShopAccounts();
};

// 加载数据
onMounted(() => {
  fetchShopAccounts();
});

// 处理状态变化
const handleStatusChange = async (shopAccount, status) => {
  try {
    await service({
      url: `/manage/shop-accounts/${shopAccount.id}/status`,
      method: 'put',
      data: {
        status: status
      }
    });
    ElMessage.success('状态更新成功');
  } catch (error) {
    ElMessage.error('状态更新失败：' + (error.message || '未知错误'));
    console.error('状态更新失败:', error);
    // 恢复原始状态
    shopAccount.status = status === 'active' ? 'inactive' : 'active';
  }
};

// 显示添加店务账号弹窗
const showAddAccountDialog = () => {
  addAccountDialogVisible.value = true;
  // 重置搜索条件
  addAccountSearchParams.value = {
    phone: ''
  };
  // 重置分页
  memberCurrentPage.value = 1;
  memberPageSize.value = 10;
  // 获取可用会员列表
  fetchAvailableMembers();
};

// 获取可用会员列表（非店务账号的会员）
const fetchAvailableMembers = async () => {
  memberLoading.value = true;
  try {
    const response = await service({
      url: '/manage/member/list',
      method: 'get',
      params: {
        page: memberCurrentPage.value,
        pageSize: memberPageSize.value,
        keyword: addAccountSearchParams.value.phone
      }
    });
    
    // 确保availableMembers始终是数组
    availableMembers.value = Array.isArray(response.data) ? response.data : [];
    memberTotal.value = response.total || 0;
  } catch (error) {
    ElMessage.error('获取会员列表失败：' + (error.message || '未知错误'));
    console.error('获取会员列表失败:', error);
    // 出错时确保availableMembers是数组
    availableMembers.value = [];
    memberTotal.value = 0;
  } finally {
    memberLoading.value = false;
  }
};

// 处理会员行点击
const handleMemberSelect = (row) => {
  if (memberTableRef.value) {
    memberTableRef.value.toggleRowSelection(row);
  }
};

// 处理会员分页大小变化
const handleMemberSizeChange = (size) => {
  memberPageSize.value = size;
  memberCurrentPage.value = 1;
  fetchAvailableMembers();
};

// 处理会员当前页变化
const handleMemberCurrentChange = (page) => {
  memberCurrentPage.value = page;
  fetchAvailableMembers();
};

// 确认添加店务账号
const confirmAddAccount = async () => {
  if (!memberTableRef.value) return;
  
  // 获取选中的会员
  const selectedMembers = memberTableRef.value.getSelectionRows();
  if (selectedMembers.length === 0) {
    ElMessage.warning('请选择要添加的会员');
    return;
  }
  
  try {
    // 批量添加店务账号
    for (const member of selectedMembers) {
      await service({
        url: '/manage/shop-accounts/add',
        method: 'post',
        data: {
          member_id: member.id
        }
      });
    }
    
    ElMessage.success('添加店务账号成功');
    // 关闭弹窗
    addAccountDialogVisible.value = false;
    // 刷新店务账号列表
    fetchShopAccounts();
  } catch (error) {
    ElMessage.error('添加店务账号失败：' + (error.message || '未知错误'));
    console.error('添加店务账号失败:', error);
  }
};

// 处理删除店务账号
const handleDeleteAccount = async (shopAccount) => {
  try {
    // 显示确认对话框
    await ElMessageBox.confirm(
      `确定要删除店务账号 "${shopAccount.name}" 吗？`,
      '删除确认',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }
    );
    
    // 调用删除API
    await service({
      url: `/manage/shop-accounts/${shopAccount.id}`,
      method: 'delete'
    });
    
    ElMessage.success('删除店务账号成功');
    // 刷新店务账号列表
    fetchShopAccounts();
  } catch (error) {
    if (error === 'cancel') {
      // 用户取消删除操作，不显示错误提示
      return;
    }
    ElMessage.error('删除店务账号失败：' + (error.message || '未知错误'));
    console.error('删除店务账号失败:', error);
  }
};
</script>

<style scoped>
.shop-accounts-list {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.card-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.card-filter {
  margin-bottom: 20px;
  padding: 10px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.card-table {
  margin-bottom: 20px;
}

.card-footer {
  display: flex;
  justify-content: flex-end;
  padding: 10px 0;
}
</style>
